import { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

interface Props {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  icon?: ReactNode;
  eyebrow?: string;
}

export const PageHeader = ({ title, subtitle, actions, icon, eyebrow }: Props) => (
  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      alignItems: { xs: 'flex-start', md: 'center' },
      justifyContent: 'space-between',
      flexDirection: { xs: 'column', md: 'row' },
      gap: 2,
      mb: { xs: 2.25, md: 3 },
      minHeight: { md: 72 },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1.25, sm: 1.8 },
        minWidth: 0,
        width: { xs: '100%', md: actions ? 'auto' : '100%' },
        maxWidth: '100%',
        flex: { md: actions ? '1 1 auto' : '0 1 auto' },
      }}
    >
      {icon && (
        <Box
          sx={{
            width: { xs: 48, sm: 58 },
            height: { xs: 48, sm: 58 },
            borderRadius: '12px',
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            background: 'linear-gradient(145deg, rgba(255,255,255,.98), rgba(239,248,249,.98))',
            color: 'primary.main',
            border: '1px solid rgba(14,116,144,.13)',
            boxShadow: '0 16px 34px -25px rgba(15,42,61,.52)',
            '& svg': { fontSize: { xs: 25, sm: 29 } },
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1, maxWidth: '100%' }}>
        {eyebrow && (
          <Typography variant="overline" color="primary.main" sx={{ display: 'block', lineHeight: 1.4 }}>
            {eyebrow}
          </Typography>
        )}
        <Typography
          component="h1"
          variant="h4"
          sx={{
            fontWeight: 850,
            fontSize: { xs: 26, sm: 30, xl: 34 },
            lineHeight: 1.12,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65, maxWidth: 720, overflowWrap: 'anywhere' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    {actions && (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          justifyContent: { xs: 'flex-start', md: 'flex-end' },
          width: { xs: '100%', md: 'auto' },
          maxWidth: '100%',
          flex: { md: '0 0 auto' },
          flexShrink: 0,
          p: { xs: 0, md: 0.45 },
          borderRadius: '14px',
          bgcolor: { xs: 'transparent', md: 'rgba(255,255,255,.42)' },
          border: { xs: 'none', md: '1px solid rgba(14,116,144,.08)' },
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          '& .MuiButton-root': { flex: { xs: '1 1 auto', sm: '0 0 auto' } },
        }}
      >
        {actions}
      </Box>
    )}
  </Box>
);
